import React, { useState, useRef, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col ,Spinner} from "reactstrap";
import Select, { components } from "react-select";
import styles from "modules/Library/AdverseEvent/css/adverseEvent.module.scss";
import ac from "../css/accessioning.module.scss";
import CommonButton from "Components/Common/CommonButton";
import NewAccessioningModal from "../components/NewSessionModal";
import GenerateReportModal from "../components/GenerateReportModal";
import { ACCESSIONING_PATHS } from "Routes/paths";
import { useSessionService } from "modules/Session/Session/api/useSession";
import {
  useScanningScanSampleCollectionService,
  useScanningScanSampleSeperationService,
  useScanSampleCollectionSummarySelectAllService,
  useScanSampleSeperationSummarySelectAllService,
  useAccessioningSessionScanlogProgressHistoryService,
} from "modules/ScanningApp/api/useScanningSession";
import sf from "assets/scss/common/searchFilter.module.scss";
import { showToastMessage } from "common/service/CommonService";
import FullPageLoader from "Components/Common/FullPageLoader";
import { accessioningStatsCards } from "../helper/accessioningconstant";
import FilePreviewModal from "modules/Study/Attachments/components/FilePreviewModal";
import { useGlobalStudy } from "context/StudyContext";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import NoStudySelectedPlaceholder from "Components/Common/NoStudySelectedPlaceholder";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import {
  StudySelectDetailData,
  StudyPeriodDetail,
  StudyTimepointDetail,
  StudyAliquotDetail,
} from "../helper/session.types";
import SessionsTab from "../components/SessionsTab";
import SampleCollectionHistoryTab from "../components/SampleCollectionHistoryTab";
import SampleSeparationHistoryTab from "../components/SampleSeparationHistoryTab";
import ScanProgressHistoryTab from "../components/ScanProgressHistoryTab";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";

/* ------------------------------------------------------------------ */
/* Stat Card Component                                                */
/* ------------------------------------------------------------------ */
interface StatCardProps {
  title: string;
  value: number | string;
  subtext?: string;
  icon: React.ReactNode;
  subtextIcon?: string;
  subtextCls?: string;
  bgColor?: string;
  iconColor?: string;
  trendType?: "info" | "negative" | "success";
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtext,
  icon,
  subtextIcon,
  bgColor = "#ecfdf5",
  iconColor = "#10b981",
  trendType = "info",
}) => (
  <div className={styles.statCard}>
    <div
      className={styles.iconWrapper}
      style={{ backgroundColor: bgColor, color: iconColor }}
    >
      {icon}
    </div>
    <div className={styles.label}>{title}</div>
    <div className={styles.value}>{value}</div>
    {subtext && (
      <div className={`${styles.trend} ${styles[trendType]}`}>
        <i className={subtextIcon ?? "ri-information-line"} />
        {subtext}
      </div>
    )}
  </div>
);



/* ------------------------------------------------------------------ */
/* Main Component                                                     */
/* ------------------------------------------------------------------ */
const AccessioningList: React.FC = () => {
  document.title = "Session | ZENTIXS VISTA";
  
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedStudy: globalStudy } = useGlobalStudy();

  // Tab State: "sessions" | "sampleCollection" | "sampleSeparation" | "scanProgressHistory"
  const [activeMainTab, setActiveMainTab] = useState<
    "sessions" | "sampleCollection" | "sampleSeparation" | "scanProgressHistory"
  >("sessions");

  // Filters State
  const [searchText, setSearchText] = useState<string>("");
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("ALL");
  const [selectedTimepoints, setSelectedTimepoints] = useState<any[]>([
    { value: "ALL", label: "All Timepoints" },
  ]);
  const [selectedAliquot, setSelectedAliquot] = useState<string>("ALL");
  const [selectedDosingStation, setSelectedDosingStation] = useState<string>("ALL");
  const [currentPageNumber, setcurrentPageNumber] = useState<number>(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // Reset page when filters change
  const prevStudyRef = useRef(globalStudy?.studyid);
  useEffect(() => {
    if (prevStudyRef.current !== globalStudy?.studyid) {
      prevStudyRef.current = globalStudy?.studyid;
      setcurrentPageNumber(1);
    }
  }, [globalStudy?.studyid]);

  // Refetch and reset page/search when switching tabs
  useEffect(() => {
    setcurrentPageNumber(1);
    setSearchText("");
    if (activeMainTab === "sessions") {
      SelectAllQuery.refetch();
    }
  }, [activeMainTab]);

  // Audit State
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  // Primary API Query for Tab 1 (Sessions)
  const { SelectAllQuery, UpdateMutation } = useSessionService({
    searchtext: searchText,
    currentpage: currentPageNumber,
    pagesize: 10,
    studyid: globalStudy?.studyid,
    enabled: true,
  });

  // Query for Collection Summary API: ScanSampleCollection/CollectionSummarySelectAll
  const selectedPeriodId = selectedPeriod !== "ALL" ? Number(selectedPeriod) : null;
  const selectedTimepointId = useMemo(() => {
    if (!selectedTimepoints || selectedTimepoints.length === 0) return null;
    if (selectedTimepoints.some((opt: any) => opt.value === "ALL")) return null;
    return selectedTimepoints.map((opt: any) => Number(opt.value));
  }, [selectedTimepoints]);
  const selectedAliquotId = selectedAliquot !== "ALL" ? Number(selectedAliquot) : null;
  const selectedDosingStationNo = selectedDosingStation !== "ALL" ? Number(selectedDosingStation) : null;

  const { SelectAllQuery: collectionSummaryQuery } =
    useScanSampleCollectionSummarySelectAllService({
      studyid: globalStudy?.studyid ? Number(globalStudy.studyid) : 0,
      periodid: selectedPeriodId,
      timepointid: selectedTimepointId,
      dosingstationno: selectedDosingStationNo,
      searchtext: searchText || "",
      currentpage: currentPageNumber,
      pagesize: 10,
      enabled: activeMainTab === "sampleCollection" && !!globalStudy?.studyid,
    });

  // Query for Separation Summary API: ScanSampleSeperation/Seperation/Summary/SelectAll
  const { SelectAllQuery: seperationSummaryQuery } =
    useScanSampleSeperationSummarySelectAllService({
      studyid: globalStudy?.studyid ? Number(globalStudy.studyid) : 0,
      periodid: selectedPeriodId,
      timepointid: selectedTimepointId,
      aliquotid: selectedAliquotId,
      dosingstationno: selectedDosingStationNo,
      searchtext: searchText || "",
      currentpage: currentPageNumber,
      pagesize: 10,
      enabled: activeMainTab === "sampleSeparation" && !!globalStudy?.studyid,
    });

  // Query for Scanlog Progress History API: AccessioningSession/ScanlogProgressHistory
  const { SelectAllQuery: scanlogProgressQuery } =
    useAccessioningSessionScanlogProgressHistoryService({
      studyid: globalStudy?.studyid ? Number(globalStudy.studyid) : 0,
      periodid: selectedPeriodId,
      timepointid: selectedTimepointId,
      dosingstationno: selectedDosingStationNo,
      tabstatus: "ALL",
      searchtext: searchText || "",
      currentpage: currentPageNumber,
      pagesize: 10,
      enabled: activeMainTab === "scanProgressHistory" && !!globalStudy?.studyid,
    });

  // Queries for Collection & Separation logs
  const { SelectAllQuery: collectionQuery } = useScanningScanSampleCollectionService({
    searchtext: searchText,
    currentpage: currentPageNumber,
    pagesize: 10,
    sessionid: 0,
  });

  const { SelectAllQuery: separationQuery } = useScanningScanSampleSeperationService({
    searchtext: searchText,
    currentpage: currentPageNumber,
    pagesize: 10,
    sessionid: 0,
  });

  const rawData = SelectAllQuery?.data?.data || [];
  const summaryItem = rawData.find((item: any) => item.summarycard);
  const sessionData = rawData.filter((item: any) => !item.summarycard);

  const summaryData = summaryItem?.summarycard || {
    total_subjects: 0,
    total_vaccutainers_scanned: 0,
    total_aliquots_scanned: 0,
    total_pending_aliquots: 0,
  };

  const onSearch = (value: string) => {
    setSearchText(value);
    setcurrentPageNumber(1);
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Session",
      studyid: globalStudy?.studyid,
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Session_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const handleResume = (row: any) => {
    const payload = {
      sessionid: row.sessionid,
      sessionstate: "active",
      updatereason: "Update Session Status",
    };

    UpdateMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage(
          "success",
          res?.message || "Session resumed successfully"
        );
        navigate(ACCESSIONING_PATHS.SESSION.replace(":id", row.sessionid));
      },
      onError: (err: any) => {
        showToastMessage("error", err || "Failed to resume session");
      },
    });
  };

  // Fetch Study details (periods and timepoints) directly from api/Study/Select
  const studyId = globalStudy?.studyid ? Number(globalStudy.studyid) : null;
  const { SelectQuery: studySelectQuery } = useMasterStudyService({
    id: studyId,
    enabledSelect: !!studyId,
  });

  const studyDetail: StudySelectDetailData | undefined =
    studySelectQuery?.data?.data?.[0];

  // Period Options populated from api/Study/Select response periods array
  const periodOptions = useMemo(() => {
    const periods = studyDetail?.periods || [];
    const opts = periods.map((p: StudyPeriodDetail) => {
      const pNo = String(p.period_number || p.period_no || p.period_id).padStart(2, "0");
      return {
        value: String(p.period_id),
        label: `Period ${pNo}`,
      };
    });
    return [{ value: "ALL", label: "All Periods" }, ...opts];
  }, [studyDetail?.periods]);

  // Timepoint Options populated from api/Study/Select response timepoints array
  const timepointOptions = useMemo(() => {
    const timepoints = studyDetail?.timepoints || [];
    const opts = timepoints.map((tp: StudyTimepointDetail) => ({
      value: tp.timepoint_id,
      label: tp.timepoint_number || String(tp.timepoint_id),
    }));
    return [{ value: "ALL", label: "All Timepoints" }, ...opts];
  }, [studyDetail?.timepoints]);

  // Dosing Station Options populated from api/Study/Select response periods array
  const dosingStationOptions = useMemo(() => {
    const stationsSet = new Set<number>();
    const periods = studyDetail?.periods || [];

    if (selectedPeriod !== "ALL") {
      const matchedPeriod = periods.find(
        (p: StudyPeriodDetail) => String(p.period_id) === String(selectedPeriod)
      );
      if (matchedPeriod?.subjectsperstation && typeof matchedPeriod.subjectsperstation === "object") {
        Object.keys(matchedPeriod.subjectsperstation).forEach((key) => {
          const numMatch = key.match(/\d+/);
          if (numMatch) {
            stationsSet.add(parseInt(numMatch[0], 10));
          }
        });
      }
    } else {
      periods.forEach((p: StudyPeriodDetail) => {
        if (p.subjectsperstation && typeof p.subjectsperstation === "object") {
          Object.keys(p.subjectsperstation).forEach((key) => {
            const numMatch = key.match(/\d+/);
            if (numMatch) {
              stationsSet.add(parseInt(numMatch[0], 10));
            }
          });
        }
      });
    }

    const sorted = Array.from(stationsSet).sort((a, b) => a - b);
    const opts = sorted.map((num) => ({
      value: String(num),
      label: `Station ${String(num).padStart(2, "0")}`,
    }));
    return [{ value: "ALL", label: "All Stations" }, ...opts];
  }, [studyDetail?.periods, selectedPeriod]);

  // Reset dosing station selection when selected period changes
  useEffect(() => {
    setSelectedDosingStation("ALL");
  }, [selectedPeriod]);

  // Aliquot Options populated from api/Study/Select response aliquots array
  const aliquotOptions = useMemo(() => {
    const aliquots = studyDetail?.aliquots || [];
    const opts = aliquots.map((a: StudyAliquotDetail) => ({
      value: String(a.aliquot_id),
      label: `Aliquot ${a.aliquot_number}`,
    }));
    return [{ value: "ALL", label: "All Aliquots" }, ...opts];
  }, [studyDetail?.aliquots]);

  // Intelligent onChange handler for timepoint multi-select
  const handleTimepointChange = (val: any) => {
    if (!val || val.length === 0) {
      setSelectedTimepoints([{ value: "ALL", label: "All Timepoints" }]);
      return;
    }
    const hadAll = selectedTimepoints.some((opt: any) => opt.value === "ALL");
    const hasAll = val.some((opt: any) => opt.value === "ALL");

    if (!hadAll && hasAll) {
      // User just clicked "All Timepoints" option -> reset to All Timepoints only
      setSelectedTimepoints([{ value: "ALL", label: "All Timepoints" }]);
    } else if (hadAll && hasAll && val.length > 1) {
      // User had "All Timepoints" and selected an individual timepoint -> remove "ALL"
      setSelectedTimepoints(val.filter((opt: any) => opt.value !== "ALL"));
    } else {
      setSelectedTimepoints(val);
    }
  };

  // Filter helper for timepoint multi-select
  const matchesTimepoints = (tpId: any, tpNum: any) => {
    if (!selectedTimepoints || selectedTimepoints.length === 0) return true;
    if (selectedTimepoints.some((opt: any) => opt.value === "ALL")) return true;
    return selectedTimepoints.some(
      (opt: any) =>
        String(opt.value) === String(tpId) ||
        String(opt.value) === String(tpNum)
    );
  };

  // Filter helper for period
  const matchesPeriod = (pId: any, pNum: any) => {
    if (selectedPeriod === "ALL") return true;
    return (
      String(selectedPeriod) === String(pId) ||
      String(selectedPeriod) === String(pNum)
    );
  };

  // Filtered dataset computed directly from API query data
  const filteredData = useMemo(() => {
    let source: any[] = [];
    if (activeMainTab === "sessions") {
      source = sessionData;
    } else if (activeMainTab === "sampleCollection") {
      source = collectionSummaryQuery?.data?.data || collectionQuery?.data?.data || [];
    } else if (activeMainTab === "sampleSeparation") {
      source = seperationSummaryQuery?.data?.data || separationQuery?.data?.data || [];
    } else if (activeMainTab === "scanProgressHistory") {
      source = scanlogProgressQuery?.data?.data || [];
    }

    return source.filter((row: any) => {
      // Period & Timepoint log filters only apply to sample collection/separation tabs
      if (activeMainTab !== "sessions" && activeMainTab !== "scanProgressHistory") {
        if (!matchesPeriod(row.periodid, row.period_number)) return false;
        if (!matchesTimepoints(row.timepointid, row.timepoint_number)) return false;
      }

      // Search text filter
      if (searchText && typeof searchText === "string" && searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        const strVal = JSON.stringify(row).toLowerCase();
        return strVal.includes(query);
      }
      return true;
    });
  }, [
    activeMainTab,
    sessionData,
    collectionSummaryQuery?.data?.data,
    collectionQuery?.data?.data,
    seperationSummaryQuery?.data?.data,
    separationQuery?.data?.data,
    scanlogProgressQuery?.data?.data,
    selectedPeriod,
    selectedTimepoints,
    searchText,
  ]);

  // Dynamically calculate total record count from API response metadata or dataset length
  const totalRecordsCount = useMemo(() => {
    switch (activeMainTab) {
      case "sessions": {
        const total = (SelectAllQuery?.data?.data?.[0] as any)?.total_count;
        return typeof total === "number" ? total : filteredData.length;
      }
      case "sampleCollection": {
        const summaryTotal = collectionSummaryQuery?.data?.data?.[0]?.total_count;
        if (summaryTotal !== undefined && summaryTotal !== null) {
          return Number(summaryTotal);
        }
        const apiTotal = (collectionQuery?.data as any)?.totalCount;
        return typeof apiTotal === "number" ? apiTotal : filteredData.length;
      }
      case "sampleSeparation": {
        const summaryTotal = seperationSummaryQuery?.data?.data?.[0]?.total_count;
        if (summaryTotal !== undefined && summaryTotal !== null) {
          return Number(summaryTotal);
        }
        const apiTotal = (separationQuery?.data as any)?.totalCount;
        return typeof apiTotal === "number" ? apiTotal : filteredData.length;
      }
      case "scanProgressHistory": {
        const summaryTotal = scanlogProgressQuery?.data?.data?.[0]?.total_count;
        if (summaryTotal !== undefined && summaryTotal !== null) {
          return Number(summaryTotal);
        }
        return filteredData.length;
      }
      default:
        return filteredData.length;
    }
  }, [
    activeMainTab,
    SelectAllQuery?.data,
    collectionSummaryQuery?.data?.data,
    seperationSummaryQuery?.data?.data,
    scanlogProgressQuery?.data?.data,
    collectionQuery?.data,
    separationQuery?.data,
    filteredData.length,
  ]);
const {hasPermission}= usePermission()
  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <div className={`${styles.dashboardContainer} ${ac.listPageContainer}`}>
      {UpdateMutation.isPending && (
        <FullPageLoader message="Updating Session Status..." />
      )}
      <Container fluid>
        {!globalStudy?.studyid ? (
          <div>
            <NoStudySelectedPlaceholder moduleName="accessioning sessions" />
          </div>
        ) : (
          <>
            {/* ── Page Header ──────────────────────────────────────── */}
            <div className="d-flex justify-content-between align-items-center mt-4 mb-3 px-2">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>Accessioning Sessions</h5>
                </div>
                <p className={styles.pageHeaderSubtitle}>
                  Manage and monitor recent sample accessioning activities.
                </p>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.SESSION}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600"
                  style={{ height: "35px" }}
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  module={PERMISSION_MODULES.SESSION}
                  action={PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER}
                  color="primary"
                  outline
                  className="px-2 fw-600"
                  onClick={() => setModalOpen(true)}
                >
                  <i className="ri-add-line fs-18 me-1" /> New Accessioning Session
                </CommonButton>
              </div>
            </div>

            {/* ── Stat Cards Bar (Always Visible on Top) ───────────── */}
            <Row className="g-3 mb-3 px-1">
              {accessioningStatsCards({
                subjects: summaryData.total_subjects,
                vacutainers: summaryData.total_vaccutainers_scanned,
                aliquots: summaryData.total_aliquots_scanned,
                pending: summaryData.total_pending_aliquots,
              }).map((stat, idx) => (
                <Col md={3} key={idx}>
                  <StatCard
                    title={stat.title}
                    value={stat.value}
                    icon={<i className={stat.icon} />}
                    bgColor={stat.bgColor}
                    iconColor={stat.iconColor}
                    trendType={stat.trendType as any}
                    subtextIcon={stat.subtextIcon}
                  />
                </Col>
              ))}
            </Row>

            {/* ── 4 Main Tabs Navigation Bar (Compact Pill-style) ── */}
            <div className="mb-3">
              <div
                className="bg-light rounded-pill d-inline-flex border"
                style={{ padding: "3px", borderColor: "#cbd5e1" }}
              >
                {[
                  { key: "sessions", label: "Sessions", icon: "ri-layout-grid-line", hasPermission: hasPermission(PERMISSION_MODULES.SESSION, `${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER},${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`) },
                  { key: "sampleCollection", label: "Sample Collection", icon: "ri-user-shared-line", hasPermission: hasPermission(PERMISSION_MODULES.SESSION, PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER) },
                  { key: "sampleSeparation", label: "Sample Separation", icon: "ri-flask-line", hasPermission: hasPermission(PERMISSION_MODULES.SESSION, PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT) },
                  { key: "scanProgressHistory", label: "Scan Log Progress History", icon: "ri-history-line", hasPermission: hasPermission(PERMISSION_MODULES.SESSION, `${PERMISSION_ACTIONS.SUBJECT_TO_VACUTAINER},${PERMISSION_ACTIONS.VACUTAINER_TO_ALIQUOT}`) },
                ].filter((tab) => tab.hasPermission).map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`btn btn-sm px-2.5 py-1 rounded-pill fw-semibold d-flex align-items-center ${activeMainTab === tab.key ? "text-primary shadow-sm" : "text-muted"
                      }`}
                    style={{
                      fontSize: "12px",
                      lineHeight: "1.2",
                      backgroundColor: activeMainTab === tab.key ? "#fff" : "transparent",
                      border: activeMainTab === tab.key ? "1px solid #cbd5e1" : "1px solid transparent",
                      transition: "all 0.15s ease",
                    }}
                    onClick={() => setActiveMainTab(tab.key as any)}
                  >
                    <i className={`${tab.icon} fs-13 me-1`} />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Table Container Panel ───────────────────────────── */}
            <div className={styles.listPanel}>
              {/* Panel Header & Filter Controls */}
              <div className="p-3 px-4 border-bottom bg-white d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h2 className="mb-0 fs-16 fw-bold text-dark d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                  <i className="ri-test-tube-line text-primary fs-18" />
                  {activeMainTab === "sessions" && "Recent Sessions"}
                  {activeMainTab === "sampleCollection" && "Subject to Vacutainer Log"}
                  {activeMainTab === "sampleSeparation" && "Vacutainer to Aliquot Log"}
                  {activeMainTab === "scanProgressHistory" && "Scanning Progress History"}
                </h2>

                <div className="d-flex flex-wrap align-items-center gap-1.5" style={{ gap: "6px" }}>
                  {/* Period Filter (For Tabs 2, 3, 4) */}
                  {activeMainTab !== "sessions" && (
                    <div style={{ width: "130px" }}>
                      <Select
                        options={periodOptions}
                        value={periodOptions.find((opt) => opt.value === selectedPeriod)}
                        onChange={(val: any) => setSelectedPeriod(val?.value || "ALL")}
                        placeholder="Select Period"
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={{
                          control: (provided: any) => ({
                            ...provided,
                            minHeight: "32px",
                            height: "32px",
                            fontSize: "12px",
                            borderRadius: "6px",
                            borderColor: "#cbd5e1",
                            boxShadow: "none",
                          }),
                          valueContainer: (provided: any) => ({
                            ...provided,
                            padding: "0 8px",
                          }),
                          dropdownIndicator: (provided: any) => ({
                            ...provided,
                            padding: "2px 6px",
                          }),
                          menu: (provided: any) => ({
                            ...provided,
                            fontSize: "12px",
                            borderRadius: "6px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            marginTop: "4px",
                          }),
                          menuList: (provided: any) => ({
                            ...provided,
                            padding: "4px 0",
                            maxHeight: "180px",
                          }),
                          option: (provided: any, state: any) => ({
                            ...provided,
                            fontSize: "12px",
                            padding: "5px 10px",
                            cursor: "pointer",
                            backgroundColor: state.isSelected || state.isFocused
                              ? "#eff6ff"
                              : "transparent",
                            color: "#1e293b",
                            fontWeight: state.isSelected ? "600" : "400",
                          }),
                          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>
                  )}

                  {/* Dosing Station Filter (For Tabs 2, 3, 4) */}
                  {activeMainTab !== "sessions" && (
                    <div style={{ width: "130px" }}>
                      <Select
                        options={dosingStationOptions}
                        value={dosingStationOptions.find((opt) => opt.value === selectedDosingStation)}
                        onChange={(val: any) => {
                          setSelectedDosingStation(val?.value || "ALL");
                          setcurrentPageNumber(1);
                        }}
                        placeholder="Select Station"
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={{
                          control: (provided: any) => ({
                            ...provided,
                            minHeight: "32px",
                            height: "32px",
                            fontSize: "12px",
                            borderRadius: "6px",
                            borderColor: "#cbd5e1",
                            boxShadow: "none",
                          }),
                          valueContainer: (provided: any) => ({
                            ...provided,
                            padding: "0 8px",
                          }),
                          dropdownIndicator: (provided: any) => ({
                            ...provided,
                            padding: "2px 6px",
                          }),
                          menu: (provided: any) => ({
                            ...provided,
                            fontSize: "12px",
                            borderRadius: "6px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            marginTop: "4px",
                          }),
                          menuList: (provided: any) => ({
                            ...provided,
                            padding: "4px 0",
                            maxHeight: "180px",
                          }),
                          option: (provided: any, state: any) => ({
                            ...provided,
                            fontSize: "12px",
                            padding: "5px 10px",
                            cursor: "pointer",
                            backgroundColor: state.isSelected || state.isFocused
                              ? "#eff6ff"
                              : "transparent",
                            color: "#1e293b",
                            fontWeight: state.isSelected ? "600" : "400",
                          }),
                          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>
                  )}

                  {/* Timepoint Multi-Select Dropdown Filter (For Tabs 2, 3, 4) */}
                  {activeMainTab !== "sessions" && (
                    <div style={{ width: "130px" }}>
                      <Select
                        isMulti
                        options={timepointOptions}
                        value={selectedTimepoints}
                        onChange={handleTimepointChange}
                        placeholder="Filter Timepoint..."
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        components={{
                          MultiValue: (props: any) => {
                            if (props.data?.value === "ALL") {
                              return (
                                <div
                                  style={{
                                    color: "#1e293b",
                                    fontSize: "12px",
                                    padding: "0 4px",
                                    fontWeight: "400",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {props.data.label}
                                </div>
                              );
                            }
                            return <components.MultiValue {...props} />;
                          },
                        }}
                        styles={{
                          control: (provided: any) => ({
                            ...provided,
                            minHeight: "32px",
                            height: "32px",
                            fontSize: "11px",
                            borderRadius: "6px",
                            borderColor: "#cbd5e1",
                            boxShadow: "none",
                          }),
                          valueContainer: (provided: any) => ({
                            ...provided,
                            padding: "0 6px",
                            gap: "1px",
                            flexWrap: "nowrap",
                            overflowX: "auto",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                          }),
                          multiValue: (provided: any) => ({
                            ...provided,
                            backgroundColor: "#f1f5f9",
                            borderRadius: "3px",
                            margin: "0 1px",
                            border: "1px solid #e2e8f0",
                            flexShrink: 0,
                          }),
                          multiValueLabel: (provided: any) => ({
                            ...provided,
                            color: "#1e293b",
                            fontSize: "10.5px",
                            fontWeight: "600",
                            padding: "0 3px",
                            lineHeight: "1.3",
                          }),
                          multiValueRemove: (provided: any) => ({
                            ...provided,
                            color: "#64748b",
                            padding: "0 2px",
                            cursor: "pointer",
                            ":hover": {
                              backgroundColor: "#e2e8f0",
                              color: "#0f172a",
                            },
                          }),
                          dropdownIndicator: (provided: any) => ({
                            ...provided,
                            padding: "2px 4px",
                          }),
                          clearIndicator: (provided: any) => ({
                            ...provided,
                            padding: "2px 4px",
                            display: selectedTimepoints.some((opt: any) => opt.value === "ALL")
                              ? "none"
                              : "flex",
                          }),
                          menu: (provided: any) => ({
                            ...provided,
                            fontSize: "12px",
                            borderRadius: "6px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            marginTop: "4px",
                            width: "max-content",
                            minWidth: "100%",
                          }),
                          menuList: (provided: any) => ({
                            ...provided,
                            padding: "4px 0",
                            maxHeight: "180px",
                          }),
                          option: (provided: any, state: any) => ({
                            ...provided,
                            fontSize: "12px",
                            padding: "5px 10px",
                            cursor: "pointer",
                            backgroundColor: state.isSelected || state.isFocused
                              ? "#eff6ff"
                              : "transparent",
                            color: "#1e293b",
                            fontWeight: state.isSelected ? "600" : "400",
                          }),
                          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>
                  )}

                  {/* Aliquot Dropdown Filter (For Sample Separation Tab) */}
                  {activeMainTab === "sampleSeparation" && (
                    <div style={{ width: "130px" }}>
                      <Select
                        options={aliquotOptions}
                        value={aliquotOptions.find((opt) => opt.value === selectedAliquot)}
                        onChange={(val: any) => setSelectedAliquot(val?.value || "ALL")}
                        placeholder="Select Aliquot"
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={{
                          control: (provided: any) => ({
                            ...provided,
                            minHeight: "32px",
                            height: "32px",
                            fontSize: "12px",
                            borderRadius: "6px",
                            borderColor: "#cbd5e1",
                            boxShadow: "none",
                          }),
                          valueContainer: (provided: any) => ({
                            ...provided,
                            padding: "0 8px",
                          }),
                          dropdownIndicator: (provided: any) => ({
                            ...provided,
                            padding: "2px 6px",
                          }),
                          menu: (provided: any) => ({
                            ...provided,
                            fontSize: "12px",
                            borderRadius: "6px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            marginTop: "4px",
                          }),
                          menuList: (provided: any) => ({
                            ...provided,
                            padding: "4px 0",
                            maxHeight: "180px",
                          }),
                          option: (provided: any, state: any) => ({
                            ...provided,
                            fontSize: "12px",
                            padding: "5px 10px",
                            cursor: "pointer",
                            backgroundColor: state.isSelected || state.isFocused
                              ? "#eff6ff"
                              : "transparent",
                            color: "#1e293b",
                            fontWeight: state.isSelected ? "600" : "400",
                          }),
                          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>
                  )}

                  {/* Search Input Filter (Compact) */}
                  <div
                    className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}
                    style={{ width: "180px", minWidth: "180px", height: "32px", marginBottom: 0, padding: "0 10px" }}
                  >
                    <i className={`ri-search-line ${sf.searchIcon}`} style={{ fontSize: "13px" }} />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchText}
                      className={sf.searchInput}
                      style={{ fontSize: "12px" }}
                      onChange={(e) => onSearch(e.target.value)}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() => setSearchFocused(false)}
                    />
                    {searchText && (
                      <i
                        className={`ri-close-line ${sf.clearIcon}`}
                        style={{ fontSize: "14px" }}
                        onClick={() => onSearch("")}
                      />
                    )}
                  </div>
                  {(activeMainTab === "sampleCollection" || activeMainTab === "sampleSeparation") && (
                    <CommonButton
                      module={PERMISSION_MODULES.SESSION}
                      color="primary"
                      outline
                      className="d-flex align-items-center px-2 fw-semibold fs-12"
                      style={{ height: "32px" }}
                      onClick={() => setIsReportModalOpen(true)}
                    >
                      <i className="ri-file-pdf-2-line text-danger me-1 fs-13" />
                      Generate Report
                    </CommonButton>
                  )}
                </div>
              </div>

              {/* Modular Tab Content Components */}
              <div className="px-4 py-2">
                {activeMainTab === "sessions" && (
                  <SessionsTab
                    data={filteredData}
                    loading={SelectAllQuery.isFetching}
                    currentPage={currentPageNumber}
                    pageSize={10}
                    totalRecords={totalRecordsCount}
                    onPageChange={setcurrentPageNumber}
                    onResume={handleResume}
                    onViewLog={(id) => navigate(ACCESSIONING_PATHS.LOGS.replace(":id", String(id)))}
                  />
                )}

                {activeMainTab === "sampleCollection" && (
                  <SampleCollectionHistoryTab
                    data={filteredData}
                    loading={collectionSummaryQuery.isFetching || collectionQuery.isFetching}
                    currentPage={currentPageNumber}
                    pageSize={10}
                    totalRecords={totalRecordsCount}
                    onPageChange={setcurrentPageNumber}
                  />
                )}

                {activeMainTab === "sampleSeparation" && (
                  <SampleSeparationHistoryTab
                    data={filteredData}
                    loading={seperationSummaryQuery.isFetching || separationQuery.isFetching}
                    currentPage={currentPageNumber}
                    pageSize={10}
                    totalRecords={totalRecordsCount}
                    onPageChange={setcurrentPageNumber}
                  />
                )}

                {activeMainTab === "scanProgressHistory" && (
                  <ScanProgressHistoryTab
                    data={filteredData}
                    loading={scanlogProgressQuery.isFetching}
                    currentPage={currentPageNumber}
                    pageSize={10}
                    totalRecords={totalRecordsCount}
                    onPageChange={setcurrentPageNumber}
                  />
                )}
              </div>
            </div>
          </>
        )}

        <NewAccessioningModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(!modalOpen)}
          isNewSessionFlow={true}
        />

        <GenerateReportModal
          isOpen={isReportModalOpen}
          toggle={() => setIsReportModalOpen(!isReportModalOpen)}
          activeMainTab={activeMainTab}
          studyId={globalStudy?.studyid ? Number(globalStudy.studyid) : null}
          periodOptions={periodOptions}
          selectedTimepointId={selectedTimepointId}
          selectedAliquotId={selectedAliquotId}
          selectedDosingStationNo={selectedDosingStationNo}
          searchText={searchText}
        />

        {/* Audit Log File Preview Modal */}
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
 
      </Container>

    </div>
  );
};

export default AccessioningList;
